import React, { Fragment, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  PrimaryButton,
  AvatarWrapper,
  AVATAR_SIZES,
  Content,
  CONTENT_TYPES,
  BUTTON_VARIANTS,
  Dialog,
  Loader,
  StatusIndicator,
  STATUS_INDICATOR_VARIANTS,
  SecondaryButton,
  TextButton,
} from "cdk-radial";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import "./style.css";
import axios from "axios";
import config from "../config/app.conf.json";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import {
  INDICATOR_COLORS,
  BUNDLE_ERROR_TITLES,
  BUNDLE_ERROR_CONTEXTS,
  BUNDLE_STATUS,
} from "../Utils/constants";
import {
  BUNDLE_DOCS,
  EDIT_BUNDLES,
  PRICING_LIST_PLANS,
  SHARE_BUNDLES,
} from "./screen";

const getButtonLoaderLabel = (status) => {
  return status === BUNDLE_STATUS.ACTIVE ? "Deactivating" : "Activating";
};

const getButtonText = (status) => {
  return status === BUNDLE_STATUS.ACTIVE ? "Deactivate" : "Activate";
};

const normalizeOrgType = (value) => {
  const v = String(value || "").toUpperCase();
  if (v === "DLR" || v === "DEALER") return "DLR";
  if (v === "OEM") return "OEM";
  return "ISV";
};

const getOrgTypeLabel = (orgType) => {
  if (orgType === "DLR") return "Dealer";
  return orgType || "ISV";
};

const getVisibilityLabel = (visibility) =>
  String(visibility || "").toLowerCase() === "private" ? "Private" : "Public";

const CUSTOM_STATUS_INDICATOR_VARIANT =
  STATUS_INDICATOR_VARIANTS.CUSTOM ?? STATUS_INDICATOR_VARIANTS.DEFAULT;

const getIndicatorColorsForOrgType = (orgType) =>
  INDICATOR_COLORS[orgType] || INDICATOR_COLORS.ISV;

const getIndicatorColorsForStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return s === "active" ? INDICATOR_COLORS.active : INDICATOR_COLORS.inactive;
};

const BundleCard = (props) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorDialogTitle, setErrorDialogTitle] = useState(
    BUNDLE_ERROR_TITLES.DEFAULT,
  );
  const [buttonLoader, setButtonLoader] = useState(false);
  const history = useHistory();
  const {
    apiBundle: {
      apis,
      name,
      description,
      id,
      weight,
      status,
      visibility,
      orgType,
      org_type,
    },
    setBundleShareName,
    setBundleShareId,
    isVersion2,
    setScreen,
    setSelectedBundle,
  } = props;
  const bundlePricingEnabled = useFeatureFlag("BUNDLE_PRICING_ENABLED");

  const normalizedOrgType = normalizeOrgType(orgType || org_type);
  const orgTypeLabel = getOrgTypeLabel(normalizedOrgType);
  const visibilityLabel = getVisibilityLabel(visibility);
  const statusLabel =
    String(status || "").toLowerCase() === BUNDLE_STATUS.ACTIVE
      ? "Active"
      : "Inactive";

  const parseErrorMessage = (
    error,
    context = BUNDLE_ERROR_CONTEXTS.DEFAULT,
  ) => {
    let serverMessage = "";

    if (error.response && error.response.data && error.response.data.message) {
      serverMessage = error.response.data.message;
    } else if (error.message) {
      serverMessage = error.message;
    } else {
      serverMessage = "An unexpected error occurred";
    }
    return `${context} - ${serverMessage}`;
  };

  const showError = (message, title = BUNDLE_ERROR_TITLES.DEFAULT) => {
    setErrorMessage(message);
    setErrorDialogTitle(title);
    setShowErrorDialog(true);
  };
  const handleEdit = () => {
    if (isVersion2) {
      setSelectedBundle(props?.apiBundle);
      setScreen(EDIT_BUNDLES);
    } else {
      let restId = [];
      let asyncId = [];

      apis.forEach((e) => {
        if (e.type === "api") {
          restId.push(e.id);
        }
      });
      apis.forEach((e) => {
        if (e.type === "async-api") {
          asyncId.push(e.id);
        }
      });
    }
  };
  const handleBundleShare = () => {
    setScreen(SHARE_BUNDLES);
    setBundleShareId(id);
    setBundleShareName(name);
  };

  const handleDocs = () => {
    setSelectedBundle(props?.apiBundle);
    setScreen(BUNDLE_DOCS);
  };
  const handlePricing = () => {
    if (isVersion2) {
      setSelectedBundle(props?.apiBundle);
      setScreen(PRICING_LIST_PLANS);
    } else {
      history.push("/pricing-form");
    }
  };

  const handleDelete = () => {
    setConfirmDialogOpen(true);
  };

  const handleRedirect = () => {
    if (isVersion2) {
      history.push("/bundle-management");
    }
  };

  const handlePatchUpdate = () => {
    let stats;
    setButtonLoader(true);
    stats =
      status === BUNDLE_STATUS.ACTIVE
        ? BUNDLE_STATUS.INACTIVE
        : BUNDLE_STATUS.ACTIVE;
    axios
      .patch(
        `${config.api.api_gateway_url}/v1/admin/bundles/${id}`,
        {
          status: stats,
        },
        {
          headers: {
            Authorization: `Bearer ${props.auth.accessToken}`,
          },
        },
      )
      .then(() => {
        setButtonLoader(false);
        handleRedirect();
      })
      .catch((error) => {
        console.log(error);
        setButtonLoader(false);
        const isDeactivate = status === BUNDLE_STATUS.ACTIVE;
        const parsedError = parseErrorMessage(
          error,
          isDeactivate
            ? BUNDLE_ERROR_CONTEXTS.DEACTIVATE
            : BUNDLE_ERROR_CONTEXTS.ACTIVATE,
        );
        const title = isDeactivate
          ? BUNDLE_ERROR_TITLES.DEACTIVATION_FAILED
          : BUNDLE_ERROR_TITLES.ACTIVATION_FAILED;
        showError(parsedError, title);
      });
  };

  const handleDeleteBundle = () => {
    axios
      .patch(
        `${config.api.api_gateway_url}/v2/admin/bundle/${id}`,
        { status: BUNDLE_STATUS.RETIRED },
        {
          headers: {
            Authorization: `Bearer ${props.auth.accessToken}`,
          },
        },
      )
      .then(() => {
        setConfirmDialogOpen(false);
        handleRedirect();
      })
      .catch((error) => {
        console.log(error);
        setConfirmDialogOpen(false);
        const parsedError = parseErrorMessage(
          error,
          BUNDLE_ERROR_CONTEXTS.DELETE,
        );
        showError(parsedError, BUNDLE_ERROR_TITLES.DELETION_FAILED);
      });
  };

  const dialogContent = (
    <Content type={CONTENT_TYPES.BODY_2}>
      This operation will delete the bundle permanently, would you like to
      continue ?
    </Content>
  );
  return !name && !description && !apis ? null : (
    <>
      {confirmDialogOpen ? (
        <Dialog
          className="share-api-dialog"
          buttonsProps={[
            {
              id: "cancel-action",
              onClick: () => setConfirmDialogOpen(false),
              text: "Cancel",
              variant: BUTTON_VARIANTS.SECONDARY,
            },
            {
              id: "share-action",
              onClick: () => handleDeleteBundle(),
              text: "Delete",
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          id="share-api-dialog"
          isOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          title="Confirm Delete"
        >
          {dialogContent}
        </Dialog>
      ) : null}

      {showErrorDialog && errorMessage ? (
        <Dialog
          className="error-details-dialog"
          buttonsProps={[
            {
              id: "error-ok-action",
              onClick: () => {
                setShowErrorDialog(false);
                setErrorMessage("");
              },
              text: "OK",
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          id="error-details-dialog"
          isOpen={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorMessage("");
          }}
          title={errorDialogTitle}
        >
          <div className="error-details">
            <div className="error-message">{errorMessage}</div>
          </div>
        </Dialog>
      ) : null}
      <Card
        data-cy={`spec-card__${name}`}
        className="bundle_spec-card"
        dataTestId="bundle_card"
      >
        <div className="bundle_card_top_div">
          <div className="bundle_card_identity">
            <div className="bundle_card_logo">
              <AvatarWrapper
                style={{
                  width: "48px",
                  height: "48px",
                  left: "16px",
                  top: "16px",
                  backgroundColor: "black",
                  dataTestId: "bundle_avtar",
                }}
                size={AVATAR_SIZES.STANDARD}
              >
                <Content
                  style={{
                    color: "white",
                    fontFamily: "roboto",
                    fontWeight: "500",
                    fontize: "18px",
                    lineHeight: "24px",
                  }}
                  dataTestId="bundle_descryption"
                >
                  CDK
                </Content>
              </AvatarWrapper>
            </div>

            <div className="bundle_card_name_block">
              <div className="bundle_card_name">{name}</div>
              <div className="bundle_card_id">Bundle ID: {id}</div>

              <div className="bundle_card_indicators">
                <StatusIndicator
                  label={`${orgTypeLabel} : ${visibilityLabel}`}
                  ariaLabel={`Bundle ${orgTypeLabel} ${visibilityLabel}`}
                  variant={CUSTOM_STATUS_INDICATOR_VARIANT}
                  className="bundle_card_status_indicator"
                  style={{
                    borderColor:
                      getIndicatorColorsForOrgType(normalizedOrgType)
                        .borderColor,
                    backgroundColor:
                      getIndicatorColorsForOrgType(normalizedOrgType)
                        .backgroundColor,
                  }}
                />
                <StatusIndicator
                  label={statusLabel}
                  ariaLabel={`Bundle ${statusLabel}`}
                  variant={CUSTOM_STATUS_INDICATOR_VARIANT}
                  className="bundle_card_status_indicator"
                  style={{
                    borderColor:
                      getIndicatorColorsForStatus(status).borderColor,
                    backgroundColor:
                      getIndicatorColorsForStatus(status).backgroundColor,
                  }}
                />
              </div>
            </div>
          </div>
          <div className="bundle_card_description">{description}</div>
          <div className="list_apis">Included APIs ({apis.length})</div>
        </div>
        <div className={"bundle_card_middle_div"}>
          <div className={"bundle_apis_scroll"}>
            {apis.map(function (api) {
              return <div className="api-font">{api.name}</div>;
            })}
          </div>
        </div>
        <div className="bundle_card_bottom_div">
          <div className="bundle_card__ruler" />

          <div className="bundle_card_footer">
            <div className="bundle_card_footer_left">
              <SecondaryButton
                dataTestId="bundle-toggle-status"
                iconColor="primary"
                text={
                  buttonLoader ? (
                    <Loader
                      label={getButtonLoaderLabel(status)}
                      variant="inline"
                    />
                  ) : (
                    getButtonText(status)
                  )
                }
                onClick={handlePatchUpdate}
                isDisabled={buttonLoader}
              />

              <TextButton
                dataTestId="bundle-delete"
                iconColor="primary"
                text="Delete"
                onClick={handleDelete}
              />
            </div>

            <div className="bundle_card_footer_right">
              <SecondaryButton
                dataTestId="bundle-edit"
                iconColor="primary"
                text="Edit"
                onClick={handleEdit}
              />

              {config?.isBundlePricePlanEnabled && (
                <SecondaryButton
                  dataTestId="bundle-pricing"
                  iconColor="primary"
                  text="Pricing"
                  onClick={handlePricing}
                />
              )}

              {config?.isBundleDocsEnabled && (
                <SecondaryButton
                  dataTestId="bundle-docs"
                  iconColor="primary"
                  text="Docs"
                  onClick={handleDocs}
                />
              )}

              {!!isVersion2 && (
                <SecondaryButton
                  dataTestId="bundle-share"
                  iconColor="primary"
                  text="Share"
                  onClick={handleBundleShare}
                  isDisabled={
                    visibility === "public" ||
                    String(status || "").toLowerCase() !== BUNDLE_STATUS.ACTIVE
                      ? true
                      : false
                  }
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

class bundleContainer extends React.Component {
  render() {
    return this.props.auth.isAuthenticated ? (
      <BundleCard {...this.props} />
    ) : (
      <div></div>
    );
  }
}
export default withAuth(bundleContainer);
